/**
 * 確定履歴選択ダイアログ (§5 確定履歴から取込 → 参照列プレビュー,
 * AC-DFD-029/030/031, s07-confirm-history-import-reference-view.md)。
 *
 * 呼び出し元 (index.tsx) が現在の断面・エリア・scope (小売) で絞り込んだ
 * DemandConfirmRecordSummary 一覧を渡す。列: 確定日時 (yyyy/MM/dd HH:mm) /
 * 対象日 / エリア / BGコード / 取得元。単一選択で「取込」ボタンが活性化する。
 *
 * Decision #5: 取込は編集中の需要想定 (draft) を一切変更せず参照列として
 * 追加するだけなので、旧「保存していない変更は失われます」dirty 警告は
 * 撤去し、hint「編集中の需要想定は変更されません。参照列として追加します」
 * に置き換えた (dirty 状態では出し分けない)。ただし選べる確定履歴が 0 件の
 * ときは空状態の案内と二重になるため出さない (2026-09-17 デザイナー指摘、
 * 詳細は hint の描画箇所のコメント)。
 */
import { useEffect, useState } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { DemandConfirmRecordSummary } from '@ebalance/proto/bffuser/v1/demand_confirm_record_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  type BoxProps,
  RadioGroupItem as EcRadioGroupItem,
  HStack,
  LightModeSemanticColors,
  PrimaryButton,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TertiaryButton,
  Text,
  Tooltip,
  VStack,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { ActionReasonText } from '~/components/common/ActionReasonText'
import { TABLE_HEADER_BG } from '~/components/common/MasterDataTable'
import { Modal } from '~/components/common/Modal'
import { RegionLoading } from '~/components/common/RegionLoading'
import { convertAreaLabel } from '~/models/area'
import { formatDateTimeFull } from '~/models/dateTime'
import { formatProtoDate } from '~/pages/DemandForecastDaily/models/sectionHelpers'

// chakra v3.29 + React 18 types の既知不具合で `RadioGroupItemProps` が
// `{ unstyled }` のみに collapse し、children を含む全 props を JSX で受け取れなくなる。
// ecloud 由来の component をそのまま使い、取りこぼした props を BoxProps で型付けし直す
// (`TradeSideRadioGroup` / `DemandMethodRadio` と同じ回避)。
const RadioGroupItem = EcRadioGroupItem as React.FC<
  BoxProps & { value: string }
>

/**
 * デザイナー指摘 (2026-08-13): table の DS 準拠 (`.ecl-table` 8080 実測、
 * `design/css/components.css:783-816`)。同じ PR で「類似日から選択」table
 * (`SimilarDayDialog.tsx` の `similarDayThStyle` / `similarDayTdStyle`) に
 * 適用した方針をそのまま踏襲する。TH 背景色は新しい定数を作らず
 * `MasterDataTable` の `TABLE_HEADER_BG` (single source) を import する。
 *
 * sticky: 親の `<Box overflowY="auto" maxHeight="400px">` (下記 return 内) に
 * 既に高さ上限があるため、SimilarDayDialog で踏んだ「maxHeight が無いと
 * sticky が効かない」問題は再発しない (同 PR で `54vh` を追加して解決した箇所)。
 */
const CONFIRM_IMPORT_TABLE_BORDER_COLOR =
  LightModeSemanticColors.Border.lowEmphasis.value
const confirmImportThStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: TABLE_HEADER_BG,
  padding: '10px 12px',
  fontWeight: 600,
  fontSize: fontSizes.md.value,
  color: LightModeSemanticColors.Text.midEmphasis.value,
  borderBottom: `1px solid ${CONFIRM_IMPORT_TABLE_BORDER_COLOR}`,
  zIndex: 1,
  whiteSpace: 'nowrap',
}
const confirmImportTdStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: `1px solid ${CONFIRM_IMPORT_TABLE_BORDER_COLOR}`,
  whiteSpace: 'nowrap',
  fontSize: fontSizes.md.value,
}

type Props = {
  isOpen: boolean
  onClose: () => void
  /**
   * 選択した確定履歴の id で取込を実行する (AC-06: 追加の確認ダイアログを
   * 挟まず、この呼び出しだけで取込が完了する)。
   */
  onImport: (recordId: string) => Promise<void>
  /** 呼び出し元が断面・エリア・scope で絞り込んだ一覧 */
  confirmRecords: DemandConfirmRecordSummary[]
  isLoading: boolean
  /**
   * 一覧の取得に失敗した (issue #12037)。 読込中・失敗・取得済みの判断は flow 側が
   * 持ち、本 dialog は `isLoading` / `isError` をそのまま描画する (再試行中は
   * `isLoading` が true で `isError` は false)。 必須 prop にして、配線の渡し忘れを
   * 型で分かるようにする。
   */
  isError: boolean
  /** 一覧の取得をやり直す */
  onRetry: () => void
  /** 取込 mutation 実行中かどうか (AC-06: ボタン活性制御・二重送信防止) */
  isImporting?: boolean
  /**
   * 参照列 5 件上限に達しているか (Decision #3)。true のとき「取込」ボタンを
   * disable し、tooltip で理由を示す (States「参照列 5 件 満杯」)。
   */
  isAtLimit?: boolean
  /**
   * すでに参照列として追加した確定履歴の id (AC-DFD-064)。選択中の確定履歴がこの
   * 集合に含まれる間は「取込」を非活性にし、押せない理由を画面上の文で示す。
   */
  addedRecordIds?: ReadonlySet<string>
}

export const ConfirmRecordImportDialog = ({
  isOpen,
  onClose,
  onImport,
  confirmRecords,
  isLoading,
  isError,
  onRetry,
  isImporting = false,
  isAtLimit = false,
  addedRecordIds,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)

  // ダイアログを開くたびに選択状態をリセットする (ConfirmDemandDialog と同パターン)
  useEffect(() => {
    if (isOpen) setSelectedId(undefined)
  }, [isOpen])

  // 取得失敗中・再取得中は、選択が残っていても取り込ませない (一覧が見えない間に
  // 古い履歴を使わない)。選択は消さないので、回復すれば続きから取り込める。
  const isListUnavailable = isLoading || isError
  const handleImport = (): void => {
    if (selectedId === undefined || isListUnavailable) return
    void onImport(selectedId)
  }

  const isAlreadyAdded =
    selectedId !== undefined && (addedRecordIds?.has(selectedId) ?? false)
  // 押せない理由は 1 つだけ出す。上限と重複が両方当たる時は上限の理由を出す
  // (上限は削除が先に必要で、重複は選び直すだけで解消するため)。
  const disabledReason: string | undefined = isAtLimit
    ? t('page:demandForecastDaily.confirmRecordImport.atLimit')
    : isAlreadyAdded
      ? t('page:demandForecastDaily.confirmRecordImport.alreadyAdded')
      : undefined
  const importButtonDisabled =
    selectedId === undefined ||
    isListUnavailable ||
    disabledReason !== undefined
  const importButton = (
    <PrimaryButton
      disabled={importButtonDisabled}
      loading={isImporting}
      onClick={handleImport}
    >
      {t('page:demandForecastDaily.confirmRecordImport.importButton')}
    </PrimaryButton>
  )

  const footer = (
    <HStack gap={3}>
      <TertiaryButton disabled={isImporting} onClick={onClose}>
        {t('common:action.cancel')}
      </TertiaryButton>
      {/* 上限・追加済みの時は、削除する・別の確定履歴を選べば押せる前提の不足のため、
          押せない理由を画面上の文で常に出し、ツールチップは補助にする
          (conventions.md §操作できない時のボタンの扱いの 2)。未選択で disabled の時は
          理由を出さない。 */}
      {disabledReason !== undefined && (
        <ActionReasonText>{disabledReason}</ActionReasonText>
      )}
      {disabledReason !== undefined ? (
        <Tooltip content={disabledReason} placement="top" showArrow>
          {importButton}
        </Tooltip>
      ) : (
        importButton
      )}
    </HStack>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={t('page:demandForecastDaily.confirmRecordImport.title')}
    >
      {/* Decision #5: 取込は draft を一切変更しない参照列追加であることを明示する。

          **選べる確定履歴が 1 件も無い時は出さない** (2026-09-17 デザイナー指摘
          「『編集中の需要想定は変更されません。参照列として追加します』 と
          『該当する確定履歴がありません』 の両方がある場合は、 二つの違う案内が
          表示されているため混乱する。 優先すべき情報だけを出す」)。
          空の時に優先すべきは「選べるものが無い」 という事実で、 参照列の追加が
          何をするかの案内は**追加できる候補がある時だけ**意味を持つ
          (Decision #5 の趣旨 = draft が書き換わると誤解させない、 は候補ゼロなら
          そもそも成立しないので narrowing は趣旨と矛盾しない)。
          読み込み中も出さない (結果が確定するまで案内の要否が決まらない)。 */}
      {!isLoading && !isError && confirmRecords.length > 0 && (
        <Alert
          marginBottom={3}
          status="info"
          title={t(
            'page:demandForecastDaily.confirmRecordImport.referenceHint',
          )}
        />
      )}
      {/* issue #12037: 一覧の取得失敗はダイアログ内に出して再試行させる (全画面エラーに
          すると編集中の需要想定が消える)。 取得失敗を「該当する確定履歴がありません」
          (0 件) と取り違えさせないため、失敗中は空状態を出さない。 文言は spec 未記載の
          ため暫定 (PdM確認 #12031 で類似日と同じ判断に揃える)。 */}
      {isError && (
        <Alert
          description={
            <VStack align="stretch" gap={2}>
              <Text>
                {t('page:demandForecastDaily.confirmRecordImport.fetchFailed')}
              </Text>
              <Box>
                <TertiaryButton onClick={onRetry} size="sm">
                  {t('page:demandForecastDaily.confirmRecordImport.retry')}
                </TertiaryButton>
              </Box>
            </VStack>
          }
          role="alert"
          status="error"
          title=""
        />
      )}
      <RegionLoading isLoading={isLoading}>
        {isLoading || isError ? null : confirmRecords.length === 0 ? (
          /* 空状態は **Alert 1 本**で出す (2026-09-18 デザイナー指摘「文字が
           小さい & 薄い、次に何をしたら良いかも一緒に表示したい」)。
           以前は `fontSize="sm"` + `Text.lowEmphasis` の素の 1 行で、状態の
           説明も次の行動も無かった。

           [[EmptyPanel]] は使わない。 あちらの docstring が使い分けを定めて
           おり、**理由が複数あって理由ごとに次の行動が変わる**面のためのもの。
           ここは理由が 1 つ (候補が無い) で、確定すれば解消するので
           「DS `Alert` 1 本で足りる」 側に当たる。
           案内を 2 つ並べない方針 (同日のデザイナー指摘) とも揃う。 */
          <Alert
            description={t(
              'page:demandForecastDaily.confirmRecordImport.emptyHint',
            )}
            status="info"
            title={t('page:demandForecastDaily.confirmRecordImport.empty')}
          />
        ) : (
          // RadioGroupRoot が table 全体の選択 (record.id) を管理する。TableRow の
          // onClick も同じ setSelectedId を呼ぶだけなので、行クリック / radio クリック
          // いずれの経路でも state は一致する (競合しない)。
          <RadioGroupRoot
            aria-label={t('page:demandForecastDaily.confirmRecordImport.title')}
            onValueChange={(details: { value: string | null }) => {
              setSelectedId(details.value ?? undefined)
            }}
            value={selectedId ?? ''}
          >
            <Box overflowY="auto" maxHeight="400px">
              <TableRoot
                aria-label={t(
                  'page:demandForecastDaily.confirmRecordImport.title',
                )}
                size="sm"
                style={{ width: '100%', borderCollapse: 'collapse' }}
              >
                <TableHeader>
                  <TableRow>
                    <TableColumnHeader style={confirmImportThStyle}>
                      {t(
                        'page:demandForecastDaily.confirmRecordImport.columnConfirmedAt',
                      )}
                    </TableColumnHeader>
                    <TableColumnHeader style={confirmImportThStyle}>
                      {t(
                        'page:demandForecastDaily.confirmRecordImport.columnTargetDate',
                      )}
                    </TableColumnHeader>
                    <TableColumnHeader style={confirmImportThStyle}>
                      {t(
                        'page:demandForecastDaily.confirmRecordImport.columnAreaCode',
                      )}
                    </TableColumnHeader>
                    <TableColumnHeader style={confirmImportThStyle}>
                      {t(
                        'page:demandForecastDaily.confirmRecordImport.columnRetailerBusinessCd',
                      )}
                    </TableColumnHeader>
                    <TableColumnHeader style={confirmImportThStyle}>
                      {t(
                        'page:demandForecastDaily.confirmRecordImport.columnProvider',
                      )}
                    </TableColumnHeader>
                    <TableColumnHeader style={confirmImportThStyle} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {confirmRecords.map((record, idx) => {
                    const isSelected = record.id === selectedId
                    return (
                      <TableRow
                        key={record.id}
                        // 選択行は zebra (灰) と別の accent の淡色 (8080
                        // `.dft-histimport-table tr.selected` = accent 8%。 類似日検索の
                        // 選択行と同じ。 UI 辞書 similar-day-search.md `tr.selected`)
                        background={
                          isSelected
                            ? 'Surface.accentPrimaryLightLow'
                            : idx % 2 === 1
                              ? 'Surface.secondary'
                              : undefined
                        }
                        _hover={{
                          background: isSelected
                            ? 'Surface.accentPrimaryLightLow'
                            : 'Hover.lowEmphasis',
                        }}
                        cursor="pointer"
                        aria-selected={isSelected}
                        onClick={() => {
                          setSelectedId(record.id)
                        }}
                      >
                        <TableCell style={confirmImportTdStyle}>
                          {record.confirmedAt !== undefined
                            ? formatDateTimeFull(
                                timestampDate(record.confirmedAt),
                              )
                            : '—'}
                        </TableCell>
                        <TableCell style={confirmImportTdStyle}>
                          {formatProtoDate(record.targetDate)}
                        </TableCell>
                        <TableCell style={confirmImportTdStyle}>
                          {convertAreaLabel(record.areaCode)}
                        </TableCell>
                        <TableCell style={confirmImportTdStyle}>
                          {record.scopeIdentifier || '—'}
                        </TableCell>
                        <TableCell style={confirmImportTdStyle}>
                          {record.providerName || '—'}
                        </TableCell>
                        <TableCell style={confirmImportTdStyle}>
                          {/* デザイナー指摘 (2026-08-13): TD 内の radio を DS の
                            RadioGroup slot (`RadioGroupItem` /
                            `RadioGroupItemHiddenInput` / `RadioGroupItemControl`)
                            に置換 (`TradeSideRadioGroup` と同じ回避パターン)。
                            checked 状態は Root の value 一致で自動判定されるため
                            item 側に checked を渡す必要はない。
                            aria-label は行ごとに一意にする (確定日時 + 取得元)。
                            title 等の共通文言だと複数行で同じ accessible name に
                            なり、テスト・スクリーンリーダー双方で行の判別ができ
                            なくなるため */}
                          <RadioGroupItem value={record.id}>
                            <RadioGroupItemHiddenInput
                              aria-label={`${
                                record.confirmedAt !== undefined
                                  ? formatDateTimeFull(
                                      timestampDate(record.confirmedAt),
                                    )
                                  : '—'
                              } ${record.providerName}`}
                            />
                            <RadioGroupItemControl />
                          </RadioGroupItem>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </TableRoot>
            </Box>
          </RadioGroupRoot>
        )}
      </RegionLoading>
    </Modal>
  )
}
